import { useCallback, useRef, useEffect, type RefObject } from 'react';

interface UseScrollFadeMaskOptions {
  /**
   * Forwarded ref to sync element assignments to.
   */
  mergeWith?: RefObject<HTMLElement | null>;
  /**
   * When false, the returned ref callback short-circuits so no ResizeObserver
   * or scroll listener is ever attached. Allows callers like HelpSheetBody
   * to keep the hook call unconditional (React rules) while opting out of
   * its work when `expandToContent` is true and the fade mask is unused.
   * Defaults to true.
   */
  enabled?: boolean;
}

/**
 * Sets data-scroll-top / data-scroll-bottom attributes on a scrollable
 * container so the CSS `.scroll-fade-y` mask only fades edges that have
 * more content to scroll toward. No React state — zero re-renders.
 *
 * Returns a callback ref — use as `ref={scrollFadeRef}` or merge with
 * an existing ref via the `mergeWith` parameter.
 */
export function useScrollFadeMask(
  mergeWithOrOptions?: RefObject<HTMLElement | null> | UseScrollFadeMaskOptions,
) {
  const options: UseScrollFadeMaskOptions =
    mergeWithOrOptions && 'current' in (mergeWithOrOptions as RefObject<HTMLElement | null>)
      ? { mergeWith: mergeWithOrOptions as RefObject<HTMLElement | null> }
      : ((mergeWithOrOptions as UseScrollFadeMaskOptions) ?? {});
  const { mergeWith, enabled = true } = options;
  const cleanupRef = useRef<(() => void) | null>(null);
  const rafRef = useRef<number | null>(null);

  const callbackRef = useCallback((el: HTMLElement | null) => {
    // Clean up previous element
    cleanupRef.current?.();
    cleanupRef.current = null;

    // Sync to the forwarded ref if provided
    if (mergeWith && 'current' in mergeWith) {
      (mergeWith as React.MutableRefObject<HTMLElement | null>).current = el;
    }

    // Short-circuit when the caller has opted out — no observers, no
    // scroll listeners, no scheduled rAFs.
    if (!enabled) return;

    if (!el) return;

    const update = () => {
      const top = el.scrollTop > 2;
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
      el.dataset.scrollTop = String(top);
      el.dataset.scrollBottom = String(bottom);
    };

    const scheduleUpdate = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        update();
      });
    };

    scheduleUpdate();
    el.addEventListener('scroll', scheduleUpdate, { passive: true });

    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(el);

    cleanupRef.current = () => {
      el.removeEventListener('scroll', scheduleUpdate);
      ro.disconnect();
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [mergeWith, enabled]);

  // Clean up on unmount
  useEffect(() => () => { cleanupRef.current?.(); }, []);

  return callbackRef;
}
